import AvTimerIcon from '@mui/icons-material/AvTimer';
import { Chip, Grid, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { paths } from 'src/paths';
import { getPlanOfCare } from 'src/redux/actions/therapyPlanning';

export const CloseIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M17 7L7 17M7 7L17 17"
        stroke="#6C737F"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
const CalenderIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M6.75 8.25H5.25V9.75H6.75V8.25ZM9.75 8.25H8.25V9.75H9.75V8.25ZM12.75 8.25H11.25V9.75H12.75V8.25ZM14.25 3H13.5V1.5H12V3H6V1.5H4.5V3H3.75C2.9175 3 2.2575 3.675 2.2575 4.5L2.25 15C2.25 15.825 2.9175 16.5 3.75 16.5H14.25C15.075 16.5 15.75 15.825 15.75 15V4.5C15.75 3.675 15.075 3 14.25 3ZM14.25 15H3.75V6.75H14.25V15Z"
        fill="#6C737F"
      />
    </svg>
  );
};

export const PencilIcon = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.24902 12.9384V15.7509H5.06152L13.3565 7.4559L10.544 4.6434L2.24902 12.9384ZM15.5315 5.2809C15.824 4.9884 15.824 4.5159 15.5315 4.2234L13.7765 2.4684C13.484 2.1759 13.0115 2.1759 12.719 2.4684L11.3465 3.8409L14.159 6.6534L15.5315 5.2809Z"
        fill="#6366F1"
      />
    </svg>
  );
};

const EocDetailsSection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const { patients, patientDetails, episodeOfCare, editEpisodeOfCare } = paths;
  const [expanded, setExpanded] = useState(true);
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const isTherapyPlanningPatient = useSelector(
    (state: any) => state?.getIsTherapyPlanningPatientReducer?.response?.data
  );

  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);

  const planOfCareResponse = useSelector((state: any) => state?.getPlanOfCareReducer);
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (state?.getPatientId) {
      dispatch(getPlanOfCare({ patientId: state?.getPatientId }));
    }
  }, [state?.getPatientId]);

  const eocStartDateWithoutTimezone =
    episodeOfCareData?.startDate && episodeOfCareData?.startDate?.split('T')?.at(0);
  const eocEndDateWithoutTimezone =
    episodeOfCareData?.endDate && episodeOfCareData?.endDate?.split('T')?.at(0);

  const eocPayerStartDateWithoutTimezone =
    episodeOfCareData?.payerPlanStartDate &&
    episodeOfCareData?.payerPlanStartDate?.split('T')?.at(0);

  const eocPayerEndDateWithoutTimezone =
    episodeOfCareData?.payerPlanEndDate && episodeOfCareData?.payerPlanEndDate?.split('T')?.at(0);

  const renderFrequencyDuration = (
    disipline: 'OT' | 'SLP' | 'PT',
    source: 'frequency' | 'duration'
  ) => {
    return (
      <Stack
        // gap="1rem"
        alignItems="center"
        flexDirection="row"
        position={'relative'}
        title={source}
      >
        <Chip
          variant="outlined"
          label={disipline}
        />
        <Box
          ml={8}
          position={'absolute'}
        >
          {source === 'frequency' ? <CalenderIcon /> : <AvTimerIcon />}
        </Box>
        <Typography
          position={'absolute'}
          ml={15}
        >
          {source === 'frequency'
            ? planOfCareDetails?.disciplineMap[disipline] || '-'
            : planOfCareDetails?.durationMap[disipline] || '-'}
        </Typography>
      </Stack>
    );
  };

  return (
    <Grid
      container
      sx={{
        justifyContent: 'space-between',
        gap: '10px',
      }}
    >
      <Grid
        p={'24px'}
        xs={12}
        lg={5.9}
        borderRadius={'20px'}
        sx={{ boxShadow: '0px 5px 22px 10px rgba(0, 0, 0, 0.04)' }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: '700',
              fontSize: '18px',
              lineHeight: '21.6px',
            }}
            variant="h6"
          >
            Basic Details
          </Typography>
        </Box>

        <Grid
          container
          sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto' }}
        >
          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography
                noWrap
                variant="subtitle2"
              >
                Episode Title
              </Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
                noWrap
              >
                {episodeOfCareData?.episodeTitle ?? '-'}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Place Of Residence</Typography>
            </Stack>
            <Stack>
              <Typography
                sx={{ color: '#6C737F' }}
                variant="body2"
              >
                {episodeOfCareData?.disciplineOfComplete
                  ?.replace(/_/g, ' ')
                  ?.replace(
                    /\w\S*/g,
                    (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                  )}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Medical Record Number</Typography>
            </Stack>
            <Stack sx={{ width: '50%' }}>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                {episodeOfCareData?.medicalRecordNumber}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Start Date</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                {eocStartDateWithoutTimezone
                  ? format(
                      new Date(
                        new Date(eocStartDateWithoutTimezone).valueOf() +
                          new Date(eocStartDateWithoutTimezone).getTimezoneOffset() * 60 * 1000
                      ),
                      'MMM dd, yyyy'
                    )
                  : '-'}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">End Date</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                {eocEndDateWithoutTimezone
                  ? format(
                      new Date(
                        new Date(eocEndDateWithoutTimezone).valueOf() +
                          new Date(eocEndDateWithoutTimezone).getTimezoneOffset() * 60 * 1000
                      ),
                      'MMM dd, yyyy'
                    )
                  : '-'}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      <Grid
        p={'24px'}
        xs={12}
        lg={5.9}
        borderRadius={'20px'}
        sx={{ boxShadow: '0px 5px 22px 10px rgba(0, 0, 0, 0.04)' }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: '700',
              fontSize: '18px',
              lineHeight: '21.6px',
            }}
            variant="h6"
          >
            Payer Details
          </Typography>
        </Box>

        <Grid
          container
          sx={{ display: 'grid', gridTemplateColumns: '200px auto' }}
        >
          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Payer Type</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
                noWrap
              >
                {episodeOfCareData?.payerType}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Payer Name</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
                noWrap
              >
                {episodeOfCareData?.payerPlanEntity?.payerPlanName}
                {episodeOfCareData?.payerPlanEntity?.active === false &&
                episodeOfCareData?.payerPlanEntity?.payerPlanName
                  ? '- Inactive'
                  : ''}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">Start Date</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                {eocPayerStartDateWithoutTimezone
                  ? format(
                      new Date(
                        new Date(eocPayerStartDateWithoutTimezone).valueOf() +
                          new Date(eocPayerStartDateWithoutTimezone).getTimezoneOffset() * 60 * 1000
                      ),
                      'MMM dd, yyyy'
                    )
                  : '-'}
              </Typography>
            </Stack>
          </Stack>

          <Stack
            flexDirection="column"
            justifyContent="space-between"
            p="12px 0px 12px 0px"
          >
            <Stack>
              <Typography variant="subtitle2">End Date</Typography>
            </Stack>
            <Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6C737F' }}
              >
                {eocPayerEndDateWithoutTimezone
                  ? format(
                      new Date(
                        new Date(eocPayerEndDateWithoutTimezone).valueOf() +
                          new Date(eocPayerEndDateWithoutTimezone).getTimezoneOffset() * 60 * 1000
                      ),
                      'MMM dd, yyyy'
                    )
                  : '-'}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EocDetailsSection;
